import { memo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'
import { UserData } from 'builder/modules/user'
import { useI18n } from 'builder/hooks/useI18n'
import { useConfig } from 'builder/hooks/useConfig'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { authService } from 'builder/services/AuthService'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import Icon24 from 'builder/components/Icon'
import { useStepByStepFeature } from 'builder/components/DocumentEditor/StepByStepFlow/hooks/useStepByStepFeature'
import { ToggleCallback } from '../../types'
import {
  DropdownContainer,
  ProfileName,
  ProfileNote,
  Links,
  Link,
  Container,
  UserDropdownTrigger,
  TriggerIcon,
  ProfileContainer,
  ProfileContent,
  LinkContainer,
  SettingsIcon,
  FaqIcon,
  LogOutIcon,
} from './styles'

interface Props {
  user: UserData
  isOpen: boolean
  onToggle: ToggleCallback
}

const UserSettingsComponent = ({ user, isOpen, onToggle }: Props) => {
  const { i18n } = useI18n()
  const config = useConfig()
  const userRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const { isRbpStepFlowActive } = useStepByStepFeature()

  useClickOutside(userRef, () => onToggle(false))

  useEscapeHandler(() => onToggle(false))

  return (
    <Container
      ref={userRef}
      isPremium={user.hasPremiumFeatures}
      data-testid="user-settings-container"
    >
      <UserDropdownTrigger
        id="acc-settings"
        isOpen={isOpen}
        onClick={() => onToggle(!isOpen)}
        data-testid="trigger"
      >
        <TriggerIcon />
      </UserDropdownTrigger>

      {isOpen && (
        <DropdownContainer>
          <ProfileContainer>
            <ProfileContent>
              <ProfileName>{user.fullName}</ProfileName>
              <ProfileNote>{user.email}</ProfileNote>
            </ProfileContent>
          </ProfileContainer>

          <Links>
            {isRbpStepFlowActive && (
              <LinkContainer
                onClick={() => {
                  trackInternalEvent('nav_click_dashboard')
                  navigate(`/`)
                }}
                data-testid="dashboard-link"
              >
                <Icon24.Grid />
                <Link>{i18n.t('builder.side_menu.dashboard')}</Link>
              </LinkContainer>
            )}

            <LinkContainer
              onClick={() => {
                trackInternalEvent('click_account_settings')
                navigate(`/account`)
              }}
              data-testid="account-settings-link"
            >
              <SettingsIcon />
              <Link>{i18n.t('builder.navbar.account_settings')}</Link>
            </LinkContainer>

            {config?.links.help && (
              <LinkContainer
                onClick={() => {
                  trackInternalEvent('click_faq')
                  window.open(config.links.help, '_blank')
                }}
                data-testid="faq-link"
              >
                <FaqIcon />
                <Link>{i18n.t('builder.navbar.faq')}</Link>
              </LinkContainer>
            )}

            {/* TODO: Change logic during the migration to JWT-auth */}
            <LinkContainer
              onClick={() => {
                // TODO: this needs to be sent differently. Currently is wrongly tracked only here.
                // trackInternalEvent('click_log_out')
                authService.logout()
              }}
              data-testid="log-out-link"
            >
              <LogOutIcon />
              <Link>{i18n.t('builder.navbar.log_out')}</Link>
            </LinkContainer>
          </Links>
        </DropdownContainer>
      )}
    </Container>
  )
}

export const UserSettings = memo(UserSettingsComponent)
